import { useMemo, useEffect, useState } from 'react'
import { DownloadsParent, DownloadsSearchResultParent } from '../models'
import { useTextMeasurements } from './use-text-measurements'
import { Variant } from '@mui/material/styles/createTypography'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'

const DEFAULT_SPACER_ICON_WIDTH = 30

interface Props {
  parentCategories: (DownloadsParent | DownloadsSearchResultParent)[]
  iconButtonRef?: React.RefObject<HTMLDivElement>
  wrapperRef: React.RefObject<HTMLDivElement>
  dotRef: React.RefObject<HTMLDivElement>
  variant?: Variant
  contentSpacer?: number
}

/**
 * Shorten the labels of breadcrumbs, beginning with the first one, until they fit within the available space
 */
export const useShortenedLabels = ({
  parentCategories,
  iconButtonRef,
  wrapperRef,
  dotRef,
  variant = 'subtitle2',
  contentSpacer = 0,
}: Props): string[] => {
  const measureTextWidth = useTextMeasurements(variant)
  const [resizeTick, setResizeTick] = useState(0)
  const appLanguage = getCurrentLanguage()
  const titles = parentCategories.map(cat => {
    return cat.id === 0 ? 'downloads.root-category-name' : cat.nameLanguages[appLanguage]
  })
  const iconButtonWidth = iconButtonRef?.current ? iconButtonRef.current.clientWidth : 0
  const parentWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 0
  const dotWidth = dotRef.current ? dotRef.current.clientWidth : 8

  const lastIdx = parentCategories.length - 1
  const combinedSpacerWidth = (parentCategories.length - 1) * DEFAULT_SPACER_ICON_WIDTH
  const availableWidth = parentWidth - iconButtonWidth - combinedSpacerWidth

  let i = 0

  useEffect(() => {
    const handleResize = (): void => setResizeTick(tick => tick + 1)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return useMemo(() => {
    let usedWidth = titles.reduce((a, b) => a + measureTextWidth(b) + contentSpacer, 0)
    const labelWidths = titles.map(title => measureTextWidth(title) + contentSpacer)

    while (usedWidth > availableWidth) {
      titles[i] = '…'
      usedWidth = titles.reduce((totalLength, currentTitle, idx) => {
        const width = currentTitle === '…' ? dotWidth : labelWidths[idx]
        return totalLength + width
      }, 0)
      i++

      if (i >= lastIdx) break
    }
    return titles
  }, [JSON.stringify(titles), availableWidth, resizeTick]) // eslint-disable-line react-hooks/exhaustive-deps
}
